import { useMemo, useCallback } from 'react';
import { BackendFilter, FilterColumn } from '../../../interfaces/sessions';
import { MultiUserSelect } from '../../../components/Selectors/MulltiUserSelector';
import { SessionAgentsColumns, BackendColumns, SESSION_METRICS_COLUMNS, CustomMetricColumns } from '../../../interfaces/table';
import { useColumnOptions } from './options/options-hook';
import { useUserData } from '../users/user-data-hook';
import { OBJECTION_COLUMNS_TO_HUMAN_READABLE, SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE } from '../../../interfaces/db';
import { CustomMetricValueType, CustomMetricSource } from '../../../interfaces/db';
import { getCustomCode, CustomCode } from '../../../components/CustomScores/cfg';

export interface FilterInfo {
  getFilterText: () => string;
  getSelectedMultiUserSelect: () => MultiUserSelect[];
  getFilteredColumns: () => BackendColumns[];
  getFilterForColumn: (column: BackendColumns) => FilterColumn | null;
  getFilterCount: () => number;
  hasFilters: () => boolean;
  getFilterSummary: () => { [key: string]: string[] };
}

export const useFilterInfo = (filter: BackendFilter): FilterInfo => {
  // Get all user-related data from centralized hook
  const { user, allVisibleUsers, sortedUserGroupInfo, allVisibleUserIds, team, customDefinitions } = useUserData();
  
  const teamId = team?.team_id || '';
  const { getColumnOptions } = useColumnOptions();
  
  // Stabilize codeOptions to prevent unnecessary re-renders
  const codeOptions = useMemo(() => {
    return getCustomCode(teamId) || [];
  }, [teamId]);

  // Helper function to check if a column is a custom metric
  const isCustomMetricColumn = (columnName: string): boolean => {
    // Check if it matches the CustomMetricColumns enum values
    const isCustomEnum = Object.values(CustomMetricColumns).includes(columnName as CustomMetricColumns) && 
           columnName !== CustomMetricColumns.SESSION_ID && 
           !columnName.startsWith('text_');
    
    // Also check if it matches the pattern metric_X (case insensitive)
    const isMetricPattern = /^metric_\d+$/i.test(columnName);
    
    return isCustomEnum || isMetricPattern;
  };

  // Helper function to get custom metric definition
  const getCustomMetricDefinition = useCallback((columnName: string) => {
    if (!isCustomMetricColumn(columnName)) return null;
    
    // Extract metric index from column name (e.g., "metric_0" -> 0, "METRIC_0" -> 0)
    const match = columnName.match(/^metric_(\d+)$/i);
    if (!match) return null;
    
    const metricIdx = parseInt(match[1], 10);
    return customDefinitions.find(def => def.metric_idx === metricIdx);
  }, [customDefinitions]);

  // Helper function to get human-readable value for custom metrics
  const getCustomMetricDisplayValue = useCallback((definition: any, value: any): string => {
    if (value === null || value === undefined) return 'Empty';
    
    if (definition.value_type === CustomMetricValueType.BOOLEAN) {
      return value === 1 || value === true ? 'True' : 'False';
    }
    
    if (definition.value_type === CustomMetricValueType.PERCENT) {
      // Convert decimal values (0.01) to percentage display (1%)
      let percentValue: number;
      if (typeof value === 'number') {
        percentValue = value * 100;
      } else {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return value.toString();
        }
        percentValue = parsed * 100;
      }
      // Round to reasonable decimal places for display
      const rounded = Math.round(percentValue * 100) / 100; // Round to 2 decimal places
      return `${rounded}%`;
    }
    
    if (definition.value_type === CustomMetricValueType.ENUM) {
      let categories: string[] = [];
      
      if (definition.value_source === CustomMetricSource.CODE && definition.value_code_str) {
        // Handle custom code categories
        const codeOption = codeOptions.find((c: CustomCode) => c.name === definition.value_code_str);
        if (codeOption?.enum_options) {
          categories = codeOption.enum_options;
        }
      } else if (definition.value_type_enum_str) {
        // Handle regular enum categories
        categories = definition.value_type_enum_str.split(',').map((c: string) => c.trim());
      }
      
      if (categories.length > 0) {
        // Handle both string and number values
        let index: number;
        if (typeof value === 'string') {
          index = parseInt(value, 10);
        } else if (typeof value === 'number') {
          index = value;
        } else {
          return value.toString();
        }
        
        if (index >= 0 && index < categories.length) {
          return categories[index];
        }
      }
    }
    
    return value.toString();
  }, [codeOptions]);

  const getSelectedMultiUserSelect = useMemo(() => (): MultiUserSelect[] => {
    const targetColumnNames = [SessionAgentsColumns.CREATOR_AGENT_ID, SessionAgentsColumns.FIRST_USER_ID];
    const filtered = filter.map((v) => v.filter((x) => targetColumnNames.includes(x.column_name as SessionAgentsColumns))).flat();
    const user_ids = Array.from(new Set(filtered.map((v) => v.value_safe).flat())) as string[];
    const allUsers = allVisibleUsers?.every((v) => user_ids.includes(v.user_id));
    
    if (user_ids.length === 0) { return []; }
    
    if (allUsers) {
      if (user?.team_id) return [{ user_group_id: null, user_id: null, team_id: user?.team_id }];
    }
    
    // check which user groups may be selected (in which case don't select those reps individually)
    const user_groups = sortedUserGroupInfo.filter((v) => {
      const visible_users = v.allUserIds.filter((x) => allVisibleUserIds.includes(x));
      if (!visible_users.length) return false;
      return visible_users.every((x) => user_ids.includes(x));
    });
    
    // only select user_groups that have no parent selected
    const user_groups_to_select = user_groups.filter((v) => {
      const all_user_group_ids = user_groups.map((x) => x.group.user_group_id);
      const parent_idx = v.parentIdxs.map((y) => sortedUserGroupInfo[y]).find((v) => v && all_user_group_ids.includes(v.group.user_group_id));
      return !parent_idx;
    });
    
    const user_group_user_ids = user_groups_to_select.map((v) => v.allUserIds).flat();
    const remaining_individuals = user_ids.filter((v) => !user_group_user_ids.includes(v));
    
    return [
      ...user_groups_to_select.map((v) => ({ user_group_id: v.group.user_group_id, user_id: null, team_id: null })),
      ...remaining_individuals.map((v) => ({ user_group_id: null, user_id: v, team_id: null }))
    ];
  }, [filter, allVisibleUsers, allVisibleUserIds, sortedUserGroupInfo, user]);

  const getFilteredColumns = useMemo(() => (): BackendColumns[] => {
    const columns = new Set<BackendColumns>();
    filter.forEach(andGroup => {
      andGroup.forEach(filterCol => {
        columns.add(filterCol.column_name as BackendColumns);
      });
    });
    return Array.from(columns);
  }, [filter]);

  const getFilterForColumn = useMemo(() => (column: BackendColumns): FilterColumn | null => {
    for (const andGroup of filter) {
      for (const filterCol of andGroup) {
        if (filterCol.column_name === column) {
          return filterCol;
        }
      }
    }
    return null;
  }, [filter]);

  const getFilterCount = useMemo(() => (): number => {
    return filter.reduce((count, andGroup) => count + andGroup.length, 0);
  }, [filter]);

  const hasFilters = useMemo(() => (): boolean => {
    return filter.length > 0 && filter.some(andGroup => andGroup.length > 0);
  }, [filter]);

  const getFilterSummary = useMemo(() => (): { [key: string]: string[] } => {
    const summary: { [key: string]: string[] } = {};
    
    filter.forEach(andGroup => {
      andGroup.forEach(filterCol => {
        const columnName = filterCol.column_name;
        
        if (!summary[columnName]) {
          summary[columnName] = [];
        }
        
        // Check if this is a custom metric column
        if (isCustomMetricColumn(columnName)) {
          const definition = getCustomMetricDefinition(columnName);
          
          if (definition) {
            // Handle different filter operators and values for custom metrics
            if (filterCol.operator === 'NEQ' && filterCol.value_safe === null) {
              summary[columnName].push('Not Empty');
            } else if (filterCol.operator === 'EQ' && filterCol.value_safe === null) {
              summary[columnName].push('Empty');
            } else if (filterCol.operator === 'GT' && typeof filterCol.value_safe === 'number') {
              const displayValue = getCustomMetricDisplayValue(definition, filterCol.value_safe);
              summary[columnName].push(`> ${displayValue}`);
            } else if (filterCol.operator === 'GE' && typeof filterCol.value_safe === 'number') {
              const displayValue = getCustomMetricDisplayValue(definition, filterCol.value_safe);
              summary[columnName].push(`>= ${displayValue}`);
            } else if (filterCol.operator === 'LT' && typeof filterCol.value_safe === 'number') {
              const displayValue = getCustomMetricDisplayValue(definition, filterCol.value_safe);
              summary[columnName].push(`< ${displayValue}`);
            } else if (filterCol.operator === 'LE' && typeof filterCol.value_safe === 'number') {
              const displayValue = getCustomMetricDisplayValue(definition, filterCol.value_safe);
              summary[columnName].push(`<= ${displayValue}`);
            } else if (Array.isArray(filterCol.value_safe)) {
              const values = filterCol.value_safe.map(value => getCustomMetricDisplayValue(definition, value));
              summary[columnName].push(...values);
            } else if (filterCol.value_safe !== null && filterCol.value_safe !== undefined) {
              const displayValue = getCustomMetricDisplayValue(definition, filterCol.value_safe);
              summary[columnName].push(displayValue);
            }
          } else {
            // Fallback for custom metrics without definitions
            if (Array.isArray(filterCol.value_safe)) {
              summary[columnName].push(...filterCol.value_safe.map(v => v.toString()));
            } else if (filterCol.value_safe !== null && filterCol.value_safe !== undefined) {
              summary[columnName].push(filterCol.value_safe.toString());
            }
          }
        } else {
          // Handle non-custom metric columns (existing logic)
          const columnOptions = getColumnOptions(columnName as BackendColumns);
          
          // Handle different filter operators and values
          if (filterCol.operator === 'NEQ' && filterCol.value_safe === null) {
            // Handle "is not null" filters - these often represent conditions like "bridged calls"
            if (columnName === 'first_user_id') {
              summary[columnName].push('Bridged');
            } else {
              summary[columnName].push('Not Empty');
            }
          } else if (filterCol.operator === 'EQ' && filterCol.value_safe === null) {
            // Handle "is null" filters
            if (columnName === 'first_user_id') {
              summary[columnName].push('Not Bridged');
            } else {
              summary[columnName].push('Empty');
            }
          } else if (filterCol.operator === 'GT' && typeof filterCol.value_safe === 'number') {
            // Handle "greater than" filters (like call duration > 90 seconds)
            const option = columnOptions.find(opt => opt.value === filterCol.value_safe);
            const displayValue = option ? option.label : `> ${filterCol.value_safe}`;
            summary[columnName].push(displayValue);
          } else if (filterCol.operator === 'LT' && typeof filterCol.value_safe === 'number') {
            // Handle "less than" filters
            const option = columnOptions.find(opt => opt.value === filterCol.value_safe);
            const displayValue = option ? option.label : `< ${filterCol.value_safe}`;
            summary[columnName].push(displayValue);
          } else if (filterCol.operator === 'EQ' && filterCol.value_safe === true) {
            // Handle boolean true filters
            if (columnName === SESSION_METRICS_COLUMNS.IS_ANSWERED) {
              summary[columnName].push('Answered');
            } else {
              summary[columnName].push('True');
            }
          } else if (filterCol.operator === 'EQ' && filterCol.value_safe === false) {
            // Handle boolean false filters
            if (columnName === SESSION_METRICS_COLUMNS.IS_ANSWERED) {
              summary[columnName].push('Not Answered');
            } else {
              summary[columnName].push('False');
            }
          } else if (Array.isArray(filterCol.value_safe)) {
            // For multi-select values, try to get human-readable labels
            const values = filterCol.value_safe.map(value => {
              const option = columnOptions.find(opt => opt.value === value);
              return option ? option.label : value;
            });
            summary[columnName].push(...values);
          } else if (filterCol.value_safe !== null && filterCol.value_safe !== undefined) {
            // For single values
            const option = columnOptions.find(opt => opt.value === filterCol.value_safe);
            const displayValue = option ? option.label : filterCol.value_safe.toString();
            summary[columnName].push(displayValue);
          }
        }
      });
    });
    
    return summary;
  }, [filter, getColumnOptions, getCustomMetricDefinition, getCustomMetricDisplayValue]);

  const getFilterText = useMemo(() => (): string => {
    if (!hasFilters()) return '';
    
    const selectedReps = getSelectedMultiUserSelect();
    const summary = getFilterSummary();
    const parts: string[] = [];
    
    // Add rep selection info
    if (selectedReps.length > 0) {
      if (selectedReps.length === 1 && selectedReps[0].team_id) {
        parts.push('All Reps');
      } else {
        const repNames: string[] = [];
        selectedReps.forEach(rep => {
          if (rep.user_id) {
            const user = allVisibleUsers.find(u => u.user_id === rep.user_id);
            if (user) repNames.push(user.user_name);
          } else if (rep.user_group_id) {
            const group = sortedUserGroupInfo.find(g => g.group.user_group_id === rep.user_group_id);
            if (group) repNames.push(group.group.user_group_name);
          }
        });
        if (repNames.length > 0) {
          parts.push(`Reps: ${repNames.join(', ')}`);
        }
      }
    }
    
    // Add other filter summaries
    Object.entries(summary).forEach(([columnName, values]) => {
      // Skip user-related columns as they're handled above
      if ([SessionAgentsColumns.CREATOR_AGENT_ID, SessionAgentsColumns.FIRST_USER_ID].includes(columnName as SessionAgentsColumns)) {
        return;
      }
      
      if (values.length > 0) {
        let humanReadableColumn: string;
        
        // Check if this is a custom metric column
        if (isCustomMetricColumn(columnName)) {
          const definition = getCustomMetricDefinition(columnName);
          if (definition && definition.label) {
            humanReadableColumn = definition.label;
          } else {
            // Fallback for custom metrics without definitions
            const match = columnName.match(/^metric_(\d+)$/i);
            const metricIdx = match ? match[1] : columnName;
            humanReadableColumn = `Custom Metric ${metricIdx}`;
          }
        } else {
          // Handle non-custom metric columns (existing logic)
          if (OBJECTION_COLUMNS_TO_HUMAN_READABLE.has(columnName as SESSION_METRICS_COLUMNS)) {
            humanReadableColumn = OBJECTION_COLUMNS_TO_HUMAN_READABLE.get(columnName as SESSION_METRICS_COLUMNS) || columnName;
          } else if (SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE[columnName as SESSION_METRICS_COLUMNS]) {
            humanReadableColumn = SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE[columnName as SESSION_METRICS_COLUMNS];
          } else {
            // Fallback to generic transformation for other columns
            humanReadableColumn = columnName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        }
        
        if (values.length === 1) {
          parts.push(`${humanReadableColumn}: ${values[0]}`);
        } else if (values.length <= 3) {
          parts.push(`${humanReadableColumn}: ${values.join(', ')}`);
        } else {
          parts.push(`${humanReadableColumn}: ${values.slice(0, 2).join(', ')} +${values.length - 2} more`);
        }
      }
    });
    
    return parts.join(' • ');
  }, [hasFilters, getSelectedMultiUserSelect, getFilterSummary, allVisibleUsers, sortedUserGroupInfo, getCustomMetricDefinition]);

  return useMemo(() => ({
    getFilterText,
    getSelectedMultiUserSelect,
    getFilteredColumns,
    getFilterForColumn,
    getFilterCount,
    hasFilters,
    getFilterSummary
  }), [
    getFilterText,
    getSelectedMultiUserSelect,
    getFilteredColumns,
    getFilterForColumn,
    getFilterCount,
    hasFilters,
    getFilterSummary
  ]);
}; 